import React, { memo, useEffect, useState } from "react";
import { Box, Button, Typography, IconButton, Input } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import axios from "axios";
import { api } from "../../../utils/api";

const FileUpload = memo(({ fieldName, index, handleChange, filesList }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (filesList) {
      setUploadedFiles(filesList);
    }
  }, [filesList]);

  const handleFileUpload = async (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert("Only .pdf, .doc, .docx, .xls, .xlsx files are allowed!");
        e.target.value = null;
        return;
      }
    }

    const formData = new FormData();
    for (let file of files) {
      formData.append("files", file);
    }

    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${api}/cloud/upload-admin`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const fileUrls = response?.data?.urls || [];
      setUploadedFiles(fileUrls);

      // Pass the fieldName and index back to parent
      handleChange(fieldName, index, "documents", fileUrls);
    } catch (error) {
      console.error("Error uploading files:", error);
      setErrorMessage("Failed to upload files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = () => {
    setUploadedFiles([]);
    // Clear the files in parent component as well
    handleChange(fieldName, index, "documents", []);
  };

  const getFileIcon = (url) => {
    url = Array.isArray(url) ? url[0] : url;
    const fileType = url?.split(".").pop().toLowerCase();
    if (fileType?.includes("pdf")) {
      return <PictureAsPdfIcon sx={{ color: "red", fontSize: "16px" }} />;
    }
    if (fileType?.includes("docx") || fileType?.includes("doc")) {
      return <ArticleIcon sx={{ color: "blue", fontSize: "14px" }} />;
    }
    if (
      fileType?.includes("xlsx") ||
      fileType?.includes("xls") ||
      fileType?.includes("sheet")
    ) {
      return <InsertDriveFileIcon sx={{ color: "green", fontSize: "14px" }} />;
    }
    return <DescriptionIcon sx={{ color: "gray", fontSize: "16px" }} />;
  };

  const getFileName = (url) => {
    let name = decodeURIComponent(url?.split("/").pop());
    let fileName = name?.split("_").pop();
    return fileName;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {uploadedFiles.length === 0 || uploadedFiles[0]?.length === 0 ? (
        <Box>
          <Input
            type="file"
            style={{ display: "none" }}
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            id={`file-upload-input-${fieldName}-${index}`}
            disabled={isLoading}
          />
          <Button
            component="label"
            sx={{ color: "black", textTransform: "none" }}
            startIcon={<UploadFileIcon />}
            htmlFor={`file-upload-input-${fieldName}-${index}`}
          >
            {isLoading ? "Uploading..." : "Upload"}
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            {getFileIcon(uploadedFiles[0])}
            <a
              href={uploadedFiles[0] || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "inherit",
                fontWeight: "bold",
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontSize: "12px",
                  width: "4rem",
                  color: "blue",
                  ml: "1px",
                  borderBottom: "1px solid blue",
                }}
              >
                {getFileName(uploadedFiles[0])}
              </Typography>
            </a>
            <IconButton onClick={handleFileDelete} size="small">
              <CloseIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Box>
        </Box>
      )}
      {errorMessage && (
        <Typography sx={{ color: "red", fontSize: "12px" }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
});

export default FileUpload;
